import {useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router';

/**
 * Custom hook that reads the location.state once and then resets it.
 *
 * This is useful for one-time flags passed via navigation, like `isNewProductionCost`.
 * After reading the state, it replaces the current history entry to remove the state,
 * preventing the logic from running again on re-renders, reloads, or back/forward navigation.
 *
 * Returns the state (typed as T) on first mount, or undefined if not present.
 */
export function useLocationStateAndReset<T>(): T | undefined {
  const location = useLocation();
  const navigate = useNavigate();
  const hasRunOnceRef = useRef(false);

  const [locationState, setLocationState] = useState<T>();

  useEffect(() => {
    if (hasRunOnceRef.current) return;

    const state = location.state as T | undefined;

    if (state !== undefined) {
      setLocationState(state);

      navigate(location.pathname + location.search, {
        replace: true
      });

      hasRunOnceRef.current = true;
    }
  }, [location, navigate]);

  return locationState;
}
